// components/GatewayVerificationInfo.tsx
// Drop this file next to ProfileView.tsx, then follow WIRING_INSTRUCTIONS.md
// to hook it into the Gateways tab.

import React, { useState } from 'react';
import { ShieldCheck, RefreshCw, AlertTriangle, X, ChevronDown, ChevronRight } from 'lucide-react';

// ─── Data ──────────────────────────────────────────────────────────────────

const VERIFIED_GATEWAYS = [
  'Stripe', 'Razorpay', 'Cashfree', 'Paystack', 'Flutterwave', 'Xendit',
  'Instamojo', 'Adyen', 'Checkout.com', 'Tap Payments', 'Moyasar', 'MercadoPago',
  'PAYONE', 'Nuvei', 'Fawry', 'Omise', 'SumUp', 'Peach Payments', 'Kushki',
  'PhonePe', 'Easebuzz', 'PayU', 'VNPay', 'DuitNow', 'Wise', 'Paysafe',
  '2Checkout', 'Worldpay', 'Klarna', 'Authorize.Net', 'Square', 'Braintree',
  'PayPal', 'Conekta',
];

const POLLED_GATEWAYS = [
  { name: 'Telr', extra: null },
  { name: 'HyperPay', extra: 'assigned API host' },
  { name: 'Paytm', extra: null },
  { name: 'CCAvenue', extra: null },
  { name: 'BillDesk', extra: 'client ID + signing key' },
  { name: 'Mollie', extra: null },
  { name: 'PayTabs', extra: 'regional API domain' },
  { name: 'Unzer (Heidelpay)', extra: null },
  { name: 'OpenPay', extra: null },
];

const MANUAL_GATEWAYS = ['Network International', 'Payoneer'];

const TOTAL = VERIFIED_GATEWAYS.length + POLLED_GATEWAYS.length + MANUAL_GATEWAYS.length;
const AUTO_CONFIRM_PCT = Math.round(
  ((VERIFIED_GATEWAYS.length + POLLED_GATEWAYS.length) / TOTAL) * 100
);

// ─── Small building blocks ──────────────────────────────────────────────────

function StatBlock({ value, label, color }: { value: number; label: string; color: string }) {
  return (
    <div className="flex-1 text-center">
      <p className={`text-xl font-black ${color}`}>{value}</p>
      <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mt-0.5">{label}</p>
    </div>
  );
}

function TierRow({
  icon, iconBg, iconColor, badgeBg, badgeText, title, count, description, children,
}: {
  icon: React.ReactNode; iconBg: string; iconColor: string; badgeBg: string; badgeText: string;
  title: string; count: number; description: string; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-2xl overflow-hidden bg-white">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50 transition-colors"
      >
        <div className={`w-9 h-9 rounded-xl ${iconBg} ${iconColor} flex items-center justify-center shrink-0`}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-[13px] font-extrabold text-slate-800">{title}</p>
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${badgeBg} ${badgeText}`}>
              {count}
            </span>
          </div>
          <p className="text-[11px] text-slate-500 font-medium mt-0.5">{description}</p>
        </div>
        {open
          ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
          : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
      </button>
      {open && <div className="px-4 pb-4 pt-1 border-t border-slate-100 space-y-3">{children}</div>}
    </div>
  );
}

function GatewayChip({ label, note }: { label: string; note?: string | null }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-slate-700 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-lg">
      {label}
      {note && <span className="text-slate-400 font-normal">· {note}</span>}
    </span>
  );
}

// ─── Main modal ──────────────────────────────────────────────────────────────

export function GatewayVerificationModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(15, 23, 42, 0.55)' }}
      onClick={onClose}
    >
      <div
        className="bg-slate-50 rounded-3xl shadow-2xl w-full max-w-2xl max-h-[88vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-white border-b border-slate-100 px-6 py-5 flex items-start gap-3 shrink-0">
          <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center text-violet-600 border border-violet-100 shrink-0">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-extrabold text-slate-900">Payment verification report</h2>
            <p className="text-[12px] text-slate-500 mt-0.5">
              How CloudAIRambo confirms a payment before marking an invoice paid
            </p>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">

          {/* Security summary card */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-extrabold text-slate-800">
                  {AUTO_CONFIRM_PCT}% of gateways auto-confirm safely
                </p>
                <p className="text-[11px] text-slate-500 font-medium mt-0.5">
                  {VERIFIED_GATEWAYS.length + POLLED_GATEWAYS.length} of {TOTAL} supported gateways
                  need zero manual work from you
                </p>
              </div>
            </div>
            <div className="flex items-stretch border-t border-slate-100 pt-4">
              <StatBlock value={VERIFIED_GATEWAYS.length} label="Verified" color="text-emerald-600" />
              <div className="w-px bg-slate-100" />
              <StatBlock value={POLLED_GATEWAYS.length} label="Double-checked" color="text-blue-600" />
              <div className="w-px bg-slate-100" />
              <StatBlock value={MANUAL_GATEWAYS.length} label="Manual" color="text-amber-600" />
            </div>
          </div>

          {/* Why this matters — one short paragraph, no card */}
          <p className="text-[11.5px] text-slate-500 leading-relaxed px-1">
            When someone pays, the gateway sends a background message claiming "this is paid."
            That message alone can't be trusted — anyone who found the URL could fake one. Every
            gateway below is set up so a fake message can never mark an invoice paid.
          </p>

          {/* Tier rows */}
          <div className="space-y-2.5">
            <TierRow
              icon={<ShieldCheck className="w-4.5 h-4.5" />}
              iconBg="bg-emerald-50" iconColor="text-emerald-600"
              badgeBg="bg-emerald-50" badgeText="text-emerald-700"
              title="Verified"
              count={VERIFIED_GATEWAYS.length}
              description="Cryptographically signed — we check the signature ourselves"
            >
              <p className="text-[11px] text-slate-600 leading-relaxed">
                These gateways sign every payment message with a secret key. Your account
                recomputes that signature and only trusts a message if it matches exactly —
                anything else is rejected automatically, no exceptions.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {VERIFIED_GATEWAYS.map(g => <GatewayChip key={g} label={g} />)}
              </div>
              <p className="text-[10px] text-slate-400 italic">
                Square, Braintree, PayPal & Conekta use equally strict, gateway-specific checks
                instead of a shared signature scheme.
              </p>
            </TierRow>

            <TierRow
              icon={<RefreshCw className="w-4.5 h-4.5" />}
              iconBg="bg-blue-50" iconColor="text-blue-600"
              badgeBg="bg-blue-50" badgeText="text-blue-700"
              title="Double-checked"
              count={POLLED_GATEWAYS.length}
              description="Their message is a hint only — we confirm directly with the gateway"
            >
              <p className="text-[11px] text-slate-600 leading-relaxed">
                These gateways don't sign messages reliably enough to trust alone. So the message
                is treated as a tip-off only — your account then asks the gateway's own system
                directly, using your real credentials, whether the order is actually paid.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {POLLED_GATEWAYS.map(g => <GatewayChip key={g.name} label={g.name} note={g.extra} />)}
              </div>
            </TierRow>

            <TierRow
              icon={<AlertTriangle className="w-4.5 h-4.5" />}
              iconBg="bg-amber-50" iconColor="text-amber-600"
              badgeBg="bg-amber-50" badgeText="text-amber-700"
              title="Manual"
              count={MANUAL_GATEWAYS.length}
              description="Customers can still pay — you confirm the payment yourself"
            >
              <p className="text-[11px] text-slate-600 leading-relaxed">
                Neither of these gateways publishes a trustworthy signature scheme or a reliable
                status API, so auto-confirmation isn't safe to build. Payments still work
                identically for the customer.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {MANUAL_GATEWAYS.map(g => <GatewayChip key={g} label={g} />)}
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 space-y-1 mt-1">
                <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wide">After a customer pays</p>
                <ol className="text-[11px] text-amber-800 space-y-1 pl-4 list-decimal">
                  <li>Check the gateway's own merchant dashboard</li>
                  <li>Confirm it shows settled / captured, not just authorized</li>
                  <li>Mark the invoice paid here in CloudAIRambo</li>
                </ol>
              </div>
            </TierRow>
          </div>

          {/* Guarantees footer */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2">
            <p className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">What's guaranteed</p>
            <ul className="space-y-1.5">
              {[
                'No invoice can be marked paid by a faked or guessed request, for Verified or Double-checked gateways.',
                "Confirmations are tied to your account — one merchant's webhook can never mark a different merchant's invoice paid.",
                'An invoice can only move from pending to paid once, and only if the gateway matches the one originally used.',
              ].map(t => (
                <li key={t} className="text-[11px] text-slate-600 leading-relaxed flex gap-2">
                  <span className="text-emerald-500 shrink-0">✓</span>{t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}